/**
 * Daten-Import mit Merge-Review — the React port of app/web/import.html.
 *
 * The flow of the original page is kept exactly, because it is the mental model
 * the users already have: upload a CSV → the server DIFFS it against the CMDBuild
 * stock and writes nothing → every changed entry can individually be taken over
 * ("Übernehmen") or left alone ("Behalten") → apply.
 *
 * What the port had to get right beyond the markup:
 *   - The decision map is transmitted IN FULL for every non-'unchanged' entry. A
 *     missing entry_id defaults to 'incoming' server-side, so a partial map would
 *     silently import what the user rejected.
 *   - Plans live in an in-process store with a 30-minute TTL, so `apply` can
 *     answer 404 ("Plan nicht (mehr) vorhanden — bitte CSV erneut hochladen…").
 *     That message is shown verbatim and paired with a one-click path back: the
 *     picked File is kept in state, so the same file can simply be re-compared.
 *     The page also warns BEFORE the user tries, once the TTL has elapsed.
 *   - Unknown classes cannot vanish: known classes render in schema order, then
 *     anything else the server sent, under its raw name (`ImportKlassLoose`).
 *   - Import is a write action. The route already requires `import.write`, but the
 *     component checks it again and disables every control — an auditor sees the
 *     explanation instead of a dead button.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { Banner } from '../components/Banner';
import { Chip, ChipList } from '../components/Chip';
import { Icon } from '../components/Icon';
import { KpiRow } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PermissionDenied } from '../components/RequirePermission';
import Spinner from '../components/Spinner';
import { useToast } from '../components/Toast';
import { ApiError, applyImport, errorMessage, planImport } from '../lib/api';
import { useAuth } from '../lib/auth';
import { countLabel, uniqueStrings } from '../lib/format';
import { IMPORT_KLASS_LABELS, IMPORT_KLASS_ORDER } from '../lib/types';
import type {
  ImportApplyResult,
  ImportDecision,
  ImportPlan,
  KpiCounter,
  PlanChange,
  PlanEntry,
} from '../lib/types';

import './admin/ops.css';
import { BomReview } from './import/BomReview';
import { PlanDecision } from './import/PlanDecision';
import { buildPlanPresentation, type EntryPresentation } from './import/planPresentation';
import './import/import-review.css';

/** Server-side plan TTL (app/services/import_service.py). */
const PLAN_TTL_MS = 30 * 60 * 1000;

/** How often the "plan expires at" hint is re-evaluated. */
const TICK_MS = 15_000;

function klassLabel(klass: string): string {
  const labels: Readonly<Record<string, string | undefined>> = IMPORT_KLASS_LABELS;
  return labels[klass] ?? klass;
}

const ACTION_SYMBOL: Readonly<Record<string, string>> = {
  create: '+',
  update: '~',
  unchanged: '=',
};

const ACTION_CLASS: Readonly<Record<string, string>> = {
  create: 'add',
  update: 'mod',
  unchanged: 'same',
};

/** One field-level diff line — old struck through, new highlighted. */
function DiffLine({ change }: { change: PlanChange }): ReactNode {
  return (
    <div className="diff">
      <span className="f">{change.field}:</span>{' '}
      {change.old === null ? (
        <span className="new">{change.new}</span>
      ) : (
        <>
          <span className="visually-hidden">Bisher: </span>
          <del className="old">{change.old}</del>
          <Icon name="arrow-right" size={12} className="arr" />
          <span className="visually-hidden">Neu: </span>
          <span className="new">{change.new}</span>
        </>
      )}
    </div>
  );
}

interface PlanEntryItemProps {
  entry: PlanEntry;
  presentation: EntryPresentation | undefined;
  /** null for 'unchanged' entries, which carry no decision. */
  decision: ImportDecision | null;
  disabled: boolean;
  onDecide: (entryId: string, decision: ImportDecision) => void;
}

function PlanEntryItem({ entry, presentation, decision, disabled, onDecide }: PlanEntryItemProps): ReactNode {
  const classes = ['entry', 'imp-entry', ACTION_CLASS[entry.action] ?? 'same'];
  if (decision === 'existing') classes.push('skipped');
  const title = presentation?.title ?? entry.key;
  const changes = presentation?.changes ?? entry.changes;

  return (
    <div className={classes.join(' ')}>
      <div className="sym" aria-hidden="true">
        {ACTION_SYMBOL[entry.action] ?? '?'}
      </div>
      <div className="body">
        <div className="imp-entry-title">
          <span className="key">{title}</span>
          {presentation?.version ? <Chip className="imp-version">Version {presentation.version}</Chip> : null}
        </div>
        {entry.action === 'unchanged' ? null : (
          <div className="diffs">
            {changes.length === 0 ? (
              <div className="diff">
                <span className="f">keine Feldänderungen gemeldet</span>
              </div>
            ) : (
              changes.map((change, index) => (
                <DiffLine key={`${change.field}-${index}`} change={change} />
              ))
            )}
          </div>
        )}
      </div>
      <PlanDecision
        entry={entry}
        label={`${title}${presentation?.version ? ` · Version ${presentation.version}` : ''}`}
        decision={decision}
        disabled={disabled}
        onDecide={onDecide}
      />
    </div>
  );
}

export function ImportPage(): ReactNode {
  const { has } = useAuth();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const canImport = has('import.write');

  const [file, setFile] = useState<File | null>(null);
  const [filename, setFilename] = useState<string | null>(null);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [plannedAt, setPlannedAt] = useState<number | null>(null);
  const [decisions, setDecisions] = useState<Readonly<Record<string, ImportDecision>>>({});
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [planGone, setPlanGone] = useState(false);
  const [result, setResult] = useState<ImportApplyResult | null>(null);
  const [dragging, setDragging] = useState(false);
  const [now, setNow] = useState<number>(() => Date.now());

  // Keeps the "Plan gültig bis …" hint honest without a per-second timer.
  useEffect(() => {
    if (plannedAt === null) return undefined;
    const timer = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(timer);
  }, [plannedAt]);

  const runPlan = useCallback(
    async (candidate: File): Promise<void> => {
      setFile(candidate);
      setPlanning(true);
      setPlanError(null);
      setApplyError(null);
      setPlanGone(false);
      setResult(null);
      try {
        const response = await planImport(candidate);
        setPlan(response.plan);
        setFilename(response.filename ?? candidate.name);
        setPlannedAt(Date.now());
        setNow(Date.now());
        // Default every decidable entry to 'incoming' — the server's own default.
        const next: Record<string, ImportDecision> = {};
        for (const entry of response.plan.entries) {
          if (entry.action !== 'unchanged') next[entry.entry_id] = 'incoming';
        }
        setDecisions(next);
      } catch (err) {
        setPlan(null);
        setPlannedAt(null);
        setDecisions({});
        setPlanError(errorMessage(err));
      } finally {
        setPlanning(false);
      }
    },
    [],
  );

  const onPick = useCallback(
    (candidate: File | undefined): void => {
      if (candidate === undefined || !canImport) return;
      void runPlan(candidate);
    },
    [canImport, runPlan],
  );

  const setDecision = useCallback((entryId: string, decision: ImportDecision): void => {
    setDecisions((current) => ({ ...current, [entryId]: decision }));
  }, []);

  const setAll = useCallback((decision: ImportDecision): void => {
    setDecisions((current) => {
      const next: Record<string, ImportDecision> = {};
      for (const key of Object.keys(current)) next[key] = decision;
      return next;
    });
  }, []);

  const reset = useCallback((): void => {
    setFile(null);
    setFilename(null);
    setPlan(null);
    setPlannedAt(null);
    setDecisions({});
    setPlanError(null);
    setApplyError(null);
    setPlanGone(false);
    setResult(null);
    if (inputRef.current !== null) inputRef.current.value = '';
  }, []);

  const decisionValues = useMemo<readonly ImportDecision[]>(
    () => Object.values(decisions),
    [decisions],
  );
  const incomingCount = decisionValues.filter((value) => value === 'incoming').length;
  const keepCount = decisionValues.length - incomingCount;

  const expiresAt = plannedAt === null ? null : plannedAt + PLAN_TTL_MS;
  const expired = expiresAt !== null && now >= expiresAt;

  const apply = useCallback(async (): Promise<void> => {
    if (plan === null || !canImport) return;
    setApplying(true);
    setApplyError(null);
    setPlanGone(false);
    try {
      // The FULL map for every decidable entry — see the module docstring.
      const response = await applyImport({ plan_id: plan.plan_id, decisions: { ...decisions } });
      setResult(response.result);
      setPlan(null);
      setPlannedAt(null);
      if (response.result.errors.length > 0) {
        toast.error(
          `Import mit ${countLabel(response.result.errors.length, 'Fehler', 'Fehlern')} abgeschlossen.`,
        );
      } else {
        toast.success(
          `Import angewendet: ${response.result.created.length} angelegt, ${response.result.updated.length} aktualisiert.`,
        );
      }
    } catch (err) {
      setApplyError(errorMessage(err));
      // 404 = the in-process plan expired; offer the way back to the upload.
      if (err instanceof ApiError && err.status === 404) setPlanGone(true);
    } finally {
      setApplying(false);
    }
  }, [plan, canImport, decisions, toast]);

  /** Known classes in schema order, then anything else the server sent. */
  const groups = useMemo(() => {
    if (plan === null) return [];
    const known: readonly string[] = IMPORT_KLASS_ORDER;
    const extra = uniqueStrings(plan.entries.map((entry) => entry.klass))
      .filter((klass) => !known.includes(klass))
      .sort((a, b) => a.localeCompare(b, 'de-DE'));
    return [...known, ...extra]
      .map((klass) => ({
        klass,
        label: klassLabel(klass),
        entries: plan.entries.filter(
          (entry) =>
            entry.klass === klass && (showUnchanged || entry.action !== 'unchanged'),
        ),
      }))
      .filter((group) => group.entries.length > 0);
  }, [plan, showUnchanged]);

  const presentation = useMemo(() => buildPlanPresentation(plan?.entries ?? []), [plan]);

  const untouchedTotal = useMemo<number>(() => {
    if (plan === null) return 0;
    return Object.values(plan.untouched_in_cmdb).reduce((sum, count) => sum + count, 0);
  }, [plan]);

  const counters = useMemo<readonly KpiCounter[]>(() => {
    if (plan === null) return [];
    return [
      { label: 'Einträge aus Datei', value: plan.total_rows, tone: 'neutral' },
      { label: 'Neu (+)', value: plan.creates, tone: plan.creates > 0 ? 'good' : 'neutral' },
      { label: 'Geändert (~)', value: plan.updates, tone: plan.updates > 0 ? 'warn' : 'neutral' },
      { label: 'Unverändert (=)', value: plan.unchanged, tone: 'neutral' },
      {
        label: 'Nur in CMDB (bleibt)',
        value: untouchedTotal,
        tone: 'neutral',
        title: 'Einträge im CMDB-Bestand, die in der Datei fehlen. Ein Import löscht nie.',
      },
    ];
  }, [plan, untouchedTotal]);

  // Defence in depth: the route is already guarded, but a stale bundle whose
  // route table lost the guard must not show an armed upload. Reuses the shared
  // panel so there is one wording of the refusal in the whole SPA.
  if (!canImport) return <PermissionDenied permission="import.write" />;

  // While a finished import is on screen the picker is replaced by the explicit
  // "Weitere Datei importieren" button, so the result cannot be mistaken for a
  // preview of the next file.
  const showDropZone = plan === null && result === null;

  return (
    <>
      <PageHeader
        title="Daten-Import mit Merge-Review"
        sub={
          plan !== null ? (
            <>
              {filename !== null ? <span className="imp-file">{filename}</span> : null}
              {filename !== null && expiresAt !== null ? ' · ' : null}
              {expiresAt !== null
                ? `Plan gültig bis ${new Date(expiresAt).toLocaleTimeString('de-DE')}`
                : null}
            </>
          ) : undefined
        }
        actions={
          plan !== null ? (
            <button type="button" className="ghost" onClick={reset} disabled={applying}>
              Abbrechen / neue Datei
            </button>
          ) : null
        }
      />

      {showDropZone ? (
        <>
          <button
            type="button"
            className={dragging ? 'drop hover' : 'drop'}
            onClick={() => inputRef.current?.click()}
            disabled={planning}
            onDragOver={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              onPick(event.dataTransfer.files[0]);
            }}
          >
            {/* Spans, not divs: a <button> may only contain phrasing content. */}
            <span className="imp-drop-line">
              <b>AssetRadar CSV oder CycloneDX-JSON hierher ziehen</b> oder klicken zum Auswählen
            </span>
            <span className="imp-drop-line imp-drop-hint">
              Vor dem Schreiben sehen Sie den Vergleich mit dem CMDB-Bestand.
            </span>
          </button>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,.json,text/csv,application/json,application/vnd.cyclonedx+json"
            hidden
            onChange={(event) => {
              const picked = event.target.files?.[0];
              // Reset so choosing the same file again fires 'change' again.
              event.target.value = '';
              onPick(picked);
            }}
          />
        </>
      ) : null}

      {planning ? (
        <Banner kind="info">
          <Spinner />{' '}
          {file !== null
            ? `Vergleiche »${file.name}« mit dem CMDB-Bestand…`
            : 'Vergleiche mit dem CMDB-Bestand…'}
        </Banner>
      ) : null}

      {planError !== null ? (
        <Banner
          kind="error"
          sub="Es wurde nichts geschrieben; laden Sie die korrigierte Datei erneut hoch."
        >
          Plan fehlgeschlagen: {planError}
        </Banner>
      ) : null}

      {plan !== null ? (
        <>
          {plan.errors.length > 0 ? (
            <Banner
              kind="warn"
              items={plan.errors}
              sub="Prüfen Sie die Hinweise vor dem Übernehmen."
            >
              {countLabel(plan.errors.length, 'Hinweis', 'Hinweise')} zum Import.
            </Banner>
          ) : null}

          {expired ? (
            <Banner
              kind="warn"
              sub="Pläne verfallen serverseitig nach 30 Minuten."
              items={file !== null ? undefined : ['Wählen Sie die Datei erneut aus.']}
            >
              Dieser Plan ist wahrscheinlich verfallen.{' '}
              {file !== null ? (
                <button
                  type="button"
                  className="link"
                  onClick={() => void runPlan(file)}
                  disabled={planning}
                >
                  »{file.name}« erneut vergleichen
                </button>
              ) : null}
            </Banner>
          ) : null}

          <KpiRow counters={counters} />

          {untouchedTotal > 0 ? (
            <div className="imp-untouched">
              <span className="imp-untouched-label">
                Nur im CMDB vorhanden (bleibt unangetastet):
              </span>
              <ChipList>
                {Object.entries(plan.untouched_in_cmdb)
                  .filter(([, count]) => count > 0)
                  .map(([klass, count]) => (
                    <Chip key={klass}>
                      {klassLabel(klass)}: {count}
                    </Chip>
                  ))}
              </ChipList>
            </div>
          ) : null}

          <div className="controls">
            <button
              type="button"
              onClick={() => setAll('incoming')}
              disabled={applying || decisionValues.length === 0}
            >
              Alle übernehmen
            </button>
            <button
              type="button"
              onClick={() => setAll('existing')}
              disabled={applying || decisionValues.length === 0}
            >
              Alle überspringen
            </button>
            <label className="check">
              <input
                type="checkbox"
                checked={showUnchanged}
                onChange={(event) => setShowUnchanged(event.target.checked)}
              />
              Unveränderte anzeigen
            </label>
            <div className="spacer" />
            <span className="muted">{filename ?? ''}</span>
          </div>

          {groups.length === 0 ? (
            <Banner kind="ok">
              Keine Unterschiede: Der Dateiinhalt entspricht dem CMDB-Bestand.
            </Banner>
          ) : (
            groups.map((group) => (
              <section className="klass-block" key={group.klass}>
                <h2>{group.label}</h2>
                {group.klass === 'ProduktVersionKomponente' ? (
                  <BomReview
                    groups={presentation.bomGroups}
                    decisions={decisions}
                    showUnchanged={showUnchanged}
                    disabled={applying}
                    onDecide={setDecision}
                  />
                ) : group.entries.map((entry) => (
                  <PlanEntryItem
                    key={entry.entry_id}
                    entry={entry}
                    presentation={presentation.byEntryId.get(entry.entry_id)}
                    decision={decisions[entry.entry_id] ?? null}
                    disabled={applying}
                    onDecide={setDecision}
                  />
                ))}
              </section>
            ))
          )}

          {applyError !== null ? (
            <Banner kind="error">
              Anwenden fehlgeschlagen: {applyError}
              {planGone && file !== null ? (
                <div className="imp-retry">
                  <button type="button" className="primary small" onClick={() => void runPlan(file)}>
                    »{file.name}« erneut hochladen und vergleichen
                  </button>
                </div>
              ) : null}
              {planGone && file === null ? (
                <div className="imp-retry">
                  <button type="button" className="primary small" onClick={reset}>
                    Neue Datei auswählen
                  </button>
                </div>
              ) : null}
            </Banner>
          ) : null}

          <div className="applybar">
            <button
              type="button"
              className="primary"
              onClick={() => void apply()}
              disabled={applying || incomingCount === 0}
              title={
                incomingCount === 0
                  ? 'Keine Änderung ausgewählt. Mindestens ein Eintrag muss übernommen werden.'
                  : undefined
              }
            >
              {applying ? (
                <Spinner variant="on-accent" label="Wende an…" />
              ) : (
                `Import anwenden (${countLabel(incomingCount, 'Änderung', 'Änderungen')})`
              )}
            </button>
            <span className="muted">
              {applying
                ? 'Wende an…'
                : keepCount > 0
                  ? `${keepCount} werden behalten/übersprungen`
                  : ''}
            </span>
            <div className="spacer" />
            <button type="button" className="ghost" onClick={reset} disabled={applying}>
              Abbrechen / neue Datei
            </button>
          </div>
        </>
      ) : null}

      {result !== null ? (
        <>
          <Banner kind={result.errors.length > 0 ? 'error' : 'ok'}>
            Import abgeschlossen: <b>{result.created.length}</b> angelegt,{' '}
            <b>{result.updated.length}</b> aktualisiert, {result.skipped.length}{' '}
            behalten/übersprungen
            {result.errors.length > 0 ? (
              <>
                , <b>{countLabel(result.errors.length, 'Fehler', 'Fehler')}</b>
              </>
            ) : null}
            .
          </Banner>
          {result.errors.length > 0 ? (
            <Banner kind="error" items={result.errors}>
              Diese Einträge wurden nicht geschrieben:
            </Banner>
          ) : null}
          <Banner kind="info">
            Die Änderungen werden an Dependency-Track übertragen; das Dashboard aktualisiert sich nach
            der Analyse.
          </Banner>
          <div className="row">
            <button type="button" className="primary" onClick={reset}>
              Weitere Datei importieren
            </button>
          </div>
        </>
      ) : null}
    </>
  );
}

export default ImportPage;
